import { useEffect,useMemo,useState } from "react";
import {
  Box,Button,Checkbox,CircularProgress,Dialog,DialogActions,DialogContent,DialogTitle,
  FormControl,FormControlLabel,FormGroup,FormHelperText,InputLabel,MenuItem,OutlinedInput,
  Select,Switch,Table,TableBody,TableCell,TableContainer,TableHead,TablePagination,
  TableRow,TextField,Typography
} from "@mui/material";
import { useNavigate,useSearchParams } from "react-router-dom";
import { useAppTheme } from "../../context/ThemeContext";
import { useAlert } from "../../context/AlertContext";

const getCookie=name=>{
  const value=document.cookie.split("; ").find(row=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async response=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

export default function AdminTeams(){
  const navigate=useNavigate();
  const [searchParams,setSearchParams]=useSearchParams();
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [teams,setTeams]=useState([]);
  const [categories,setCategories]=useState([]);
  const [delegates,setDelegates]=useState([]);
  const [search,setSearch]=useState("");
  const [categoryFilter,setCategoryFilter]=useState(searchParams.get("category")||"");
  const [loading,setLoading]=useState(true);
  const [saving,setSaving]=useState(false);
  const [modalOpen,setModalOpen]=useState(false);
  const [selectedTeam,setSelectedTeam]=useState(null);
  const [name,setName]=useState("");
  const [active,setActive]=useState(true);
  const [selectedCategories,setSelectedCategories]=useState([]);
  const [selectedDelegates,setSelectedDelegates]=useState([]);
  const [errors,setErrors]=useState({});
  const [page,setPage]=useState(0);
  const [rowsPerPage,setRowsPerPage]=useState(10);

  const loadData=async()=>{
    try{
      setLoading(true);

      const [teamsResponse,optionsResponse]=await Promise.all([
        fetch("/competition/teams/",{credentials:"include"}),
        fetch("/competition/teams/options/",{credentials:"include"})
      ]);

      const teamsData=await readResponse(teamsResponse);
      const optionsData=await readResponse(optionsResponse);

      if(!teamsResponse.ok) throw new Error(teamsData.error||"No se pudieron cargar los equipos.");
      if(!optionsResponse.ok) throw new Error(optionsData.error||"No se pudieron cargar las opciones de equipos.");

      const availableCategories=Array.isArray(optionsData.categories)?optionsData.categories:[];

      setTeams(Array.isArray(teamsData)?teamsData:[]);
      setCategories(availableCategories);
      setDelegates(Array.isArray(optionsData.delegates)?optionsData.delegates:[]);

      setCategoryFilter(current=>{
        if(current&&availableCategories.some(category=>category.slug===current)) return current;
        return availableCategories[0]?.slug||"";
      });
    }catch(error){
      showError(error.message);
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{
    loadData();
  },[]);

  useEffect(()=>{
    if(categoryFilter) setSearchParams({category:categoryFilter},{replace:true});
    else setSearchParams({},{replace:true});
  },[categoryFilter,setSearchParams]);

  const selectedCategory=useMemo(
    ()=>categories.find(category=>category.slug===categoryFilter)||null,
    [categories,categoryFilter]
  );

  const filteredTeams=useMemo(()=>{
    if(!categoryFilter) return [];

    const value=search.trim().toLowerCase();

    return teams.filter(team=>{
      const belongs=(team.categories||[]).some(category=>category.slug===categoryFilter);
      if(!belongs) return false;
      if(!value) return true;

      const delegateNames=(team.delegates||[]).map(delegate=>delegate.nombre_completo||delegate.correo).join(" ");

      return [team.name,delegateNames].some(field=>
        String(field||"").toLowerCase().includes(value)
      );
    });
  },[teams,search,categoryFilter]);

  useEffect(()=>{
    const maxPage=Math.max(0,Math.ceil(filteredTeams.length/rowsPerPage)-1);
    if(page>maxPage) setPage(maxPage);
  },[filteredTeams.length,rowsPerPage,page]);

  const rows=filteredTeams.slice(page*rowsPerPage,page*rowsPerPage+rowsPerPage);

  const handleCreate=()=>{
    setSelectedTeam(null);
    setName("");
    setActive(true);
    setSelectedCategories(categoryFilter?[categoryFilter]:[]);
    setSelectedDelegates([]);
    setErrors({});
    setModalOpen(true);
  };

  const handleEdit=team=>{
    setSelectedTeam(team);
    setName(team.name||"");
    setActive(team.active!==false);
    setSelectedCategories(Array.isArray(team.categories)?team.categories.map(category=>category.slug):[]);
    setSelectedDelegates(Array.isArray(team.delegates)?team.delegates.map(delegate=>delegate.correo):[]);
    setErrors({});
    setModalOpen(true);
  };

  const handleClose=()=>{
    if(saving) return;
    setModalOpen(false);
    setSelectedTeam(null);
    setName("");
    setActive(true);
    setSelectedCategories([]);
    setSelectedDelegates([]);
    setErrors({});
  };

  const validate=()=>{
    const nextErrors={};

    if(!name.trim()) nextErrors.name="Ingresa el nombre del equipo";
    if(!selectedCategories.length) nextErrors.categories="Selecciona al menos una categoría";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length===0;
  };

  const handleSave=async()=>{
    if(!validate()) return;

    try{
      setSaving(true);

      const editing=Boolean(selectedTeam);
      const payload={
        name:name.trim(),
        categories:selectedCategories,
        delegates:selectedDelegates
      };

      if(editing) payload.active=active;

      const response=await fetch(
        editing?`/competition/teams/${encodeURIComponent(selectedTeam.slug)}/`:"/competition/teams/",
        {
          method:editing?"PATCH":"POST",
          credentials:"include",
          headers:{
            "Content-Type":"application/json",
            "X-CSRFToken":getCookie("csrftoken")
          },
          body:JSON.stringify(payload)
        }
      );

      const data=await readResponse(response);

      if(!response.ok) throw new Error(data.error||"No se pudo guardar el equipo.");

      setModalOpen(false);
      setSelectedTeam(null);
      setName("");
      setActive(true);
      setSelectedCategories([]);
      setSelectedDelegates([]);
      setErrors({});

      await loadData();

      showSuccess(editing?"Equipo actualizado correctamente.":"Equipo creado correctamente.");
    }catch(error){
      showError(error.message);
    }finally{
      setSaving(false);
    }
  };

  const handleCategoryToggle=slug=>{
    setSelectedCategories(current=>
      current.includes(slug)
        ?current.filter(item=>item!==slug)
        :[...current,slug]
    );

    setErrors(current=>({...current,categories:""}));
  };

  const delegateText=team=>{
    const values=(team.delegates||[]).map(delegate=>delegate.nombre_completo||delegate.correo);
    return values.length?values.join(", "):"Sin delegado";
  };

  return (
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box sx={{display:"flex",flexDirection:{xs:"column",sm:"row"},alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",gap:1.5,mb:2.5}}>
        <Box>
          <Typography sx={{fontSize:{xs:typography.sizes.xl,md:typography.sizes.pageTitle},fontWeight:700,color:colors.text}}>
            Equipos
          </Typography>

          <Typography sx={{mt:0.4,fontSize:typography.sizes.md,color:colors.textSecondary}}>
            {selectedCategory
              ?`Equipos inscritos en ${selectedCategory.name}.`
              :"Selecciona una categoría para administrar sus equipos."}
          </Typography>
        </Box>

        <Button
          size="small"
          variant="contained"
          onClick={handleCreate}
          disabled={!categoryFilter}
          sx={{
            px:2.2,
            backgroundColor:colors.secondary,
            color:colors.tableHeaderText,
            boxShadow:"none",
            textTransform:"none",
            "&:hover":{
              backgroundColor:colors.secondaryHover,
              boxShadow:"none"
            }
          }}
        >
          Nuevo equipo
        </Button>
      </Box>

      <Box sx={{mb:1.5,display:"grid",gridTemplateColumns:{xs:"1fr",sm:"260px minmax(240px,1fr)"},gap:1.2}}>
        <FormControl size="small" fullWidth>
          <InputLabel>Categoría</InputLabel>

          <Select
            value={categoryFilter}
            label="Categoría"
            onChange={event=>{
              setCategoryFilter(event.target.value);
              setSearch("");
              setPage(0);
            }}
            MenuProps={{disableScrollLock:true}}
            sx={{backgroundColor:colors.surface}}
          >
            {categories.map(category=>(
              <MenuItem key={category.slug} value={category.slug}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          value={search}
          onChange={event=>{
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder={selectedCategory?`Buscar equipo en ${selectedCategory.name}`:"Buscar equipo"}
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root":{
              backgroundColor:colors.surface
            }
          }}
        />
      </Box>

      <Box sx={{width:"100%",border:`1px solid ${colors.border}`,borderRadius:shape.radiusLarge,overflow:"hidden",backgroundColor:colors.surface}}>
        <TableContainer sx={{overflowX:"auto"}}>
          <Table sx={{minWidth:750}}>
            <TableHead
              sx={{
                "& .MuiTableCell-root":{
                  backgroundColor:colors.tableHeader,
                  color:colors.tableHeaderText,
                  borderBottom:`1px solid ${colors.tableHeader}`,
                  fontWeight:700,
                  fontSize:typography.sizes.sm
                }
              }}
            >
              <TableRow>
                <TableCell>Equipo</TableCell>
                <TableCell>Delegados</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading&&(
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{py:5}}>
                    <CircularProgress size={24} sx={{color:colors.secondary}} />
                  </TableCell>
                </TableRow>
              )}

              {!loading&&!rows.length&&(
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{py:5}}>
                    <Typography sx={{color:colors.textSecondary,fontSize:typography.sizes.md}}>
                      {selectedCategory
                        ?`No hay equipos inscritos en ${selectedCategory.name}.`
                        :"No existen categorías disponibles."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}

              {!loading&&rows.map(team=>(
                <TableRow
                  key={team.slug}
                  sx={{
                    "&:last-child td":{borderBottom:0},
                    "&:hover":{backgroundColor:colors.backgroundSoft}
                  }}
                >
                  <TableCell>
                    <Typography sx={{fontWeight:700,color:colors.text,fontSize:typography.sizes.md}}>
                      {team.name}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{color:colors.textSecondary,fontSize:typography.sizes.md}}>
                      {delegateText(team)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{fontWeight:600,color:team.active?colors.success:colors.textSecondary,fontSize:typography.sizes.md}}>
                      {team.active?"Activo":"Inactivo"}
                    </Typography>
                  </TableCell>

                  <TableCell align="right">
                    <Box sx={{display:"flex",justifyContent:"flex-end",gap:0.7}}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={()=>
                          navigate(
                            `/admin/teams/${encodeURIComponent(team.slug)}/players?category=${encodeURIComponent(categoryFilter)}`
                          )
                        }
                        sx={{
                          color:colors.secondary,
                          textTransform:"none",
                          fontWeight:600
                        }}
                      >
                        Ver equipo
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={()=>handleEdit(team)}
                        sx={{
                          borderColor:colors.borderDark,
                          color:colors.text,
                          textTransform:"none",
                          "&:hover":{
                            borderColor:colors.secondary,
                            color:colors.secondary,
                            backgroundColor:colors.backgroundSoft
                          }
                        }}
                      >
                        Editar
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredTeams.length}
          page={page}
          onPageChange={(event,newPage)=>setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={event=>{
            setRowsPerPage(parseInt(event.target.value,10));
            setPage(0);
          }}
          rowsPerPageOptions={[5,10,25]}
          labelRowsPerPage="Filas:"
          labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
          sx={{
            color:colors.textSecondary,
            backgroundColor:colors.surface,
            borderTop:`1px solid ${colors.border}`,
            ".MuiTablePagination-toolbar":{
              minHeight:52,
              px:{xs:1,sm:2},
              flexWrap:{xs:"wrap",sm:"nowrap"},
              justifyContent:{xs:"center",sm:"flex-end"}
            },
            ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":{
              fontSize:typography.sizes.sm
            }
          }}
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={saving?undefined:handleClose}
        fullWidth
        maxWidth="sm"
        disableScrollLock
        PaperProps={{
          sx:{
            borderRadius:shape.radiusLarge,
            boxShadow:"none",
            border:`1px solid ${colors.border}`,
            backgroundColor:colors.surface,
            m:{xs:1.5,sm:3},
            width:{xs:"calc(100% - 24px)",sm:"100%"}
          }
        }}
      >
        <DialogTitle sx={{px:{xs:2,sm:2.5},pt:2.3,pb:1}}>
          <Typography sx={{fontSize:typography.sizes.xl,fontWeight:700,color:colors.text}}>
            {selectedTeam?"Editar equipo":"Nuevo equipo"}
          </Typography>

          <Typography sx={{mt:0.4,fontSize:typography.sizes.sm,color:colors.textSecondary}}>
            {selectedTeam
              ?"Actualiza las categorías y delegados del equipo."
              :"Registra el equipo y asígnale sus categorías y delegados."}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{px:{xs:2,sm:2.5},pt:"14px !important"}}>
          <TextField
            size="small"
            label="Nombre del equipo"
            value={name}
            onChange={event=>{
              setName(event.target.value);
              setErrors(current=>({...current,name:""}));
            }}
            error={Boolean(errors.name)}
            helperText={errors.name}
            fullWidth
            autoFocus
          />

          <Box sx={{mt:2}}>
            <Typography sx={{mb:0.7,fontSize:typography.sizes.sm,fontWeight:700,color:colors.text}}>
              Categorías
            </Typography>

            <Box
              sx={{
                border:`1px solid ${errors.categories?colors.error:colors.border}`,
                borderRadius:shape.radius,
                px:1.5,
                py:0.8,
                maxHeight:190,
                overflowY:"auto",
                backgroundColor:colors.surface
              }}
            >
              {!categories.length&&(
                <Typography sx={{py:1,color:colors.textSecondary,fontSize:typography.sizes.sm}}>
                  No existen categorías activas.
                </Typography>
              )}

              <FormGroup>
                {categories.map(category=>(
                  <FormControlLabel
                    key={category.slug}
                    sx={{my:0,color:colors.text}}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedCategories.includes(category.slug)}
                        onChange={()=>handleCategoryToggle(category.slug)}
                        sx={{
                          color:colors.controlInactive,
                          "&.Mui-checked":{color:colors.controlActive}
                        }}
                      />
                    }
                    label={
                      <Typography sx={{fontSize:typography.sizes.md,color:colors.text}}>
                        {category.name}
                      </Typography>
                    }
                  />
                ))}
              </FormGroup>
            </Box>

            {errors.categories&&(
              <FormHelperText error sx={{ml:1.5}}>
                {errors.categories}
              </FormHelperText>
            )}
          </Box>

          <FormControl fullWidth size="small" sx={{mt:2}}>
            <InputLabel id="team-delegates-label">Delegados</InputLabel>

            <Select
              labelId="team-delegates-label"
              multiple
              value={selectedDelegates}
              onChange={event=>{
                const value=event.target.value;
                setSelectedDelegates(typeof value==="string"?value.split(","):value);
              }}
              input={<OutlinedInput label="Delegados" />}
              renderValue={selected=>{
                const names=selected.map(correo=>{
                  const delegate=delegates.find(item=>item.correo===correo);
                  return delegate?.nombre_completo||correo;
                });

                return names.join(", ");
              }}
              MenuProps={{
                disableScrollLock:true,
                PaperProps:{
                  sx:{maxHeight:300,overflowY:"auto"}
                }
              }}
            >
              {delegates.map(delegate=>(
                <MenuItem key={delegate.correo} value={delegate.correo}>
                  <Checkbox
                    size="small"
                    checked={selectedDelegates.includes(delegate.correo)}
                    sx={{
                      color:colors.controlInactive,
                      "&.Mui-checked":{color:colors.controlActive}
                    }}
                  />

                  <Box>
                    <Typography sx={{fontSize:typography.sizes.md,fontWeight:600,color:colors.text}}>
                      {delegate.nombre_completo||delegate.correo}
                    </Typography>

                    {delegate.nombre_completo&&(
                      <Typography sx={{fontSize:typography.sizes.xs,color:colors.textSecondary}}>
                        {delegate.correo}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>

            {!delegates.length&&(
              <FormHelperText>No existen delegados activos registrados.</FormHelperText>
            )}
          </FormControl>

          {selectedTeam&&(
            <FormControlLabel
              sx={{mt:1.5,ml:0,color:colors.text}}
              control={
                <Switch
                  checked={active}
                  onChange={event=>setActive(event.target.checked)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked":{
                      color:colors.controlActive
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":{
                      backgroundColor:colors.controlActive
                    }
                  }}
                />
              }
              label={active?"Equipo activo":"Equipo inactivo"}
            />
          )}
        </DialogContent>

        <DialogActions sx={{px:{xs:2,sm:2.5},py:2,borderTop:`1px solid ${colors.border}`,gap:1}}>
          <Button
            size="small"
            onClick={handleClose}
            disabled={saving}
            sx={{
              color:colors.textSecondary,
              textTransform:"none"
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor:colors.secondary,
              color:colors.tableHeaderText,
              boxShadow:"none",
              textTransform:"none",
              "&:hover":{
                backgroundColor:colors.secondaryHover,
                boxShadow:"none"
              }
            }}
          >
            {saving
              ?"Guardando..."
              :selectedTeam
                ?"Guardar cambios"
                :"Crear equipo"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}